import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload } from '@ocean/api'
import { SortOrder, SortAs } from '@ocean/types/dist/sort'
import { SortUtils } from '@ocean/utils'
import {
  Card,
  ApiCardsDataResponseRapidElearningSpecificValues,
  ApiCardsDataResponseSurveySpecificValues,
} from 'types/card'
import { createApiAction } from 'api'
import { getCustomerSpecificCategories } from 'core/customerSpecificCategories/api/customerSpecificCategoriesService'
import { CustomerSpecificCategory } from 'core/customerSpecificCategories/types'
import { userLoggedOut } from 'core/session/slices/session'
import { CustomerSpecificCategoriesState, ApiCustomerSpecificCategoriesResponse } from './types'

const initialState: CustomerSpecificCategoriesState = {
  customerSpecificCategoryList: {},
}

const customerSpecificCategoriesSlice = createSlice({
  name: 'customerSpecificCategories',
  initialState,
  reducers: {
    customerSpecificCategoriesLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiCustomerSpecificCategoriesResponse>>
    ) {
      state.customerSpecificCategoryList = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { customerSpecificCategoriesLoaded } = customerSpecificCategoriesSlice.actions
export default customerSpecificCategoriesSlice.reducer

export const getInUseSortedCategories = createSelector(
  (categories?: CustomerSpecificCategory[]) => categories,
  categories => {
    if (!categories) return
    const allUsedNestedItems = categories.filter(({ parentId, inUse }) => parentId && inUse)
    // We have to do it because such parents can be with inUse=false, but we should use it
    const allUsedParents = new Set(allUsedNestedItems.map(({ parentId }) => parentId))
    const sortedCategories = [...categories].sort(
      SortUtils.sortByMultipleKeys(['sortCode'], SortOrder.Ascending, SortAs.Number)
    )
    const normalizedCategories = sortedCategories.reduce((result, item) => {
      const { inUse, parentId, categoryId } = item
      if (parentId || (!inUse && !allUsedParents.has(categoryId))) return result
      const nestedItems = allUsedNestedItems
        .filter(({ parentId: nestedItemParentId }) => nestedItemParentId === categoryId)
        .sort(SortUtils.sortByMultipleKeys(['sortCode'], SortOrder.Ascending, SortAs.Number))
      return [...result, item, ...nestedItems]
    }, [] as CustomerSpecificCategory[])
    return normalizedCategories
  }
)

export const getFilteredCardResultCategories = createSelector(
  (cards: Card[] | undefined, categories: CustomerSpecificCategory[]) => ({ cards, categories }),
  ({ cards = [], categories = [] }) => {
    const allCardsWithCategory = cards.filter(({ typeSpecificValues }) => {
      const { categoryId } = typeSpecificValues as
        | ApiCardsDataResponseRapidElearningSpecificValues
        | ApiCardsDataResponseSurveySpecificValues
      return !!categoryId
    })

    const allUsedCategories = new Set(
      allCardsWithCategory.map(({ typeSpecificValues }) => {
        const { categoryId } = typeSpecificValues as
          | ApiCardsDataResponseRapidElearningSpecificValues
          | ApiCardsDataResponseSurveySpecificValues
        return categoryId
      })
    )

    const normalizedCategories = categories.reduce((result, item) => {
      const { categoryId, parentId } = item

      if (!parentId) {
        const nestedItems = categories.filter(({ parentId }) => parentId === categoryId)
        const usedCategoryFound = nestedItems.some(({ categoryId }) =>
          allUsedCategories.has(categoryId)
        )
        if (usedCategoryFound) return [...result, item]
      }

      if (!allUsedCategories.has(categoryId)) return result

      return [...result, item]
    }, [] as CustomerSpecificCategory[])

    return normalizedCategories
  }
)

export const loadCustomerSpecificCategories = () => {
  return createApiAction({
    apiCalls: {
      list: getCustomerSpecificCategories,
    },
    payloadAction: customerSpecificCategoriesLoaded,
  })
}
